import { useEffect, useState } from "react";
import { ArrowUp } from "lucide-react";

/**
 * Floating "back to top" button, anchored bottom-right.
 *
 * Visibility rules:
 *   - Appears once the user has scrolled > 400px down.
 *   - Hides smoothly on scroll back to the top.
 *   - Never overlaps the sticky tools sidebar on guide/calculator pages —
 *     we sit at `bottom-6 right-6` and use a small size so it tucks under
 *     any sticky content that pins itself to the right edge.
 *
 * Accessibility:
 *   - `aria-label` exposes the action to screen readers.
 *   - Scroll uses `behavior: "smooth"` so the motion is not jarring.
 *   - Hidden from layout (`pointer-events-none`) when off-screen so it
 *     never traps focus in an invisible state.
 */
export default function BackToTop() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setVisible(window.scrollY > 400);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <button
      type="button"
      onClick={scrollToTop}
      aria-label="Back to top"
      title="Back to top"
      data-testid="back-to-top-button"
      className={`fixed bottom-6 right-6 z-40 flex h-11 w-11 items-center justify-center rounded-full bg-slate-900 text-white shadow-lg shadow-slate-900/20 transition-all duration-200 hover:bg-slate-800 hover:shadow-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2 sm:bottom-8 sm:right-8 sm:h-12 sm:w-12 ${
        visible
          ? "translate-y-0 opacity-100"
          : "pointer-events-none translate-y-3 opacity-0"
      }`}
    >
      <ArrowUp className="h-5 w-5" />
    </button>
  );
}
